import React, { useRef, useState } from 'react';
import {
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, Header, Segment } from 'semantic-ui-react';
import { dateAndTimeStringFromSeconds, monthStringFromTime } from '../utils/dateString';
import { calculateTimeTick } from '../utils/graphUtilities';

export interface RatingBand {
  y1: number;
  y2: number;
  color: string;
}

interface DataPoint {
  name: string;
  time: number;
  rating: number;
}

interface Props {
  data: DataPoint[];
  ratingBands: RatingBand[];
  yTicks: number[];
  getRatingColorStyle: (rating: number) => { color: string };
  isEnglish?: boolean;
}

const CHART_MARGIN_LEFT = 10;
const CHART_MARGIN_RIGHT = 20;
const Y_AXIS_WIDTH = 60;

const RatingGraph: React.FC<Props> = ({ data, ratingBands, yTicks: allYTicks, getRatingColorStyle, isEnglish = false }) => {
  const [refAreaLeft, setRefAreaLeft] = useState<number | undefined>(undefined);
  const [refAreaRight, setRefAreaRight] = useState<number | undefined>(undefined);
  const [zoomDomain, setZoomDomain] = useState<{ left: number; right: number } | null>(null);
  const [tapTooltip, setTapTooltip] = useState<{ time: number; rating: number } | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);

  if (data.length === 0) return null;

  const nameFromTime: { [time: number]: string } = {};
  data.forEach((d) => { nameFromTime[d.time] = d.name; });

  const fullLeft = data[0].time - 1000000;
  const fullRight = data[data.length - 1].time + 1000000;
  const visibleLeft = zoomDomain?.left ?? fullLeft;
  const visibleRight = zoomDomain?.right ?? fullRight;
  const xTick = calculateTimeTick(visibleLeft, visibleRight);
  const isZoomed = zoomDomain !== null;

  const visibleRatings = isZoomed
    ? data.filter((d) => d.time >= visibleLeft && d.time <= visibleRight).map((d) => d.rating)
    : [];
  const yDomain: [number | string, number | string] = isZoomed && visibleRatings.length > 0
    ? [Math.min(...visibleRatings) - 200, Math.max(...visibleRatings) + 200]
    : ['dataMin-200', 'dataMax+200'];
  const yTicks = isZoomed && visibleRatings.length > 0
    ? allYTicks.filter((t) => t >= (yDomain[0] as number) && t <= (yDomain[1] as number))
    : allYTicks;

  const getPlotRect = () => {
    const svgEl = chartContainerRef.current?.querySelector('svg');
    if (!svgEl) return null;
    const r = svgEl.getBoundingClientRect();
    return {
      left: r.left + CHART_MARGIN_LEFT + Y_AXIS_WIDTH,
      right: r.right - CHART_MARGIN_RIGHT,
    };
  };

  const pixelToTime = (clientX: number): number => {
    const plot = getPlotRect();
    if (!plot) return visibleLeft;
    const ratio = Math.max(0, Math.min(1, (clientX - plot.left) / (plot.right - plot.left)));
    return visibleLeft + ratio * (visibleRight - visibleLeft);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointerDownRef.current = { x: e.clientX, y: e.clientY };
    setRefAreaLeft(pixelToTime(e.clientX));
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (refAreaLeft !== undefined) setRefAreaRight(pixelToTime(e.clientX));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const down = pointerDownRef.current;
    const isTap = down !== null && Math.hypot(e.clientX - down.x, e.clientY - down.y) < 10;
    pointerDownRef.current = null;

    if (isTap) {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const timeStr = el?.closest('[data-time]')?.getAttribute('data-time');
      if (timeStr) {
        const time = Number(timeStr);
        const point = data.find((d) => d.time === time);
        if (point) {
          setTapTooltip((prev) => (prev?.time === point.time ? null : point));
        }
      } else {
        setTapTooltip(null);
      }
      setRefAreaLeft(undefined);
      setRefAreaRight(undefined);
      return;
    }

    if (refAreaLeft === undefined || refAreaRight === undefined || refAreaLeft === refAreaRight) {
      setRefAreaLeft(undefined);
      setRefAreaRight(undefined);
      return;
    }
    const left = Math.min(refAreaLeft, refAreaRight);
    const right = Math.max(refAreaLeft, refAreaRight);
    const minRange = (visibleRight - visibleLeft) * 0.02;
    if (right - left < minRange) {
      setRefAreaLeft(undefined);
      setRefAreaRight(undefined);
      return;
    }
    const margin = (right - left) * 0.05;
    setZoomDomain({ left: left - margin, right: right + margin });
    setTapTooltip(null);
    setRefAreaLeft(undefined);
    setRefAreaRight(undefined);
  };

  const zoomOut = () => {
    setZoomDomain(null);
    setRefAreaLeft(undefined);
    setRefAreaRight(undefined);
    setTapTooltip(null);
  };

  return (
    <>
      <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {isZoomed && (
          <Button
            basic={true}
            size="small"
            color="orange"
            icon="search minus"
            content={isEnglish ? 'Zoom Out' : '全期間に戻す'}
            onClick={zoomOut}
          />
        )}
        <span style={{ fontSize: '0.75em', color: '#999' }}>
          {isEnglish ? 'You can drag to zoom' : 'ドラッグでズームできます'}
        </span>
      </div>
      <div
        ref={chartContainerRef}
        style={{
          touchAction: 'none',
          userSelect: 'none',
          cursor: refAreaLeft !== undefined ? 'crosshair' : 'default',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <ResponsiveContainer width="95%" height={300}>
          <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="black" />
            {ratingBands.map((band) => (
              <ReferenceArea
                key={band.y1}
                y1={band.y1}
                y2={band.y2}
                fill={band.color}
                fillOpacity={0.5}
                stroke="none"
                ifOverflow="hidden"
              />
            ))}
            <XAxis
              type="number"
              dataKey="time"
              name="date"
              allowDataOverflow={true}
              domain={[visibleLeft, visibleRight]}
              ticks={xTick}
              tickFormatter={(time) => monthStringFromTime(time)}
            />
            <YAxis
              type="number"
              dataKey="rating"
              allowDataOverflow={true}
              domain={yDomain}
              ticks={yTicks}
              interval={0}
              width={Y_AXIS_WIDTH}
            />
            <Scatter
              name="rating"
              data={data}
              line={true}
              fill="white"
              shape={(props: any) => {
                const { cx, cy, payload } = props;
                const selected = tapTooltip?.time === payload.time;
                return (
                  <g key={payload.time}>
                    <circle cx={cx} cy={cy} r={20} fill="transparent" data-time={String(payload.time)} />
                    {selected ? (
                      <>
                        <circle cx={cx} cy={cy} r={11} fill="none" stroke="white" strokeWidth={2.5} />
                        <circle cx={cx} cy={cy} r={5} fill="white" />
                      </>
                    ) : (
                      <circle cx={cx} cy={cy} r={4} fill="white" stroke="#888" strokeWidth={1} />
                    )}
                  </g>
                );
              }}
            />
            {refAreaLeft !== undefined && refAreaRight !== undefined && (
              <ReferenceArea
                x1={Math.min(refAreaLeft, refAreaRight)}
                x2={Math.max(refAreaLeft, refAreaRight)}
                fill="#8884d8"
                fillOpacity={0.2}
                strokeOpacity={0.3}
              />
            )}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      {tapTooltip && (
        <Segment compact={true} style={{ marginTop: '4px', display: 'inline-block' }}>
          <Header as="h4" dividing={true}>{nameFromTime[tapTooltip.time]}</Header>
          <div>{dateAndTimeStringFromSeconds(tapTooltip.time)}</div>
          <div>
            Rating:&nbsp;
            <span style={getRatingColorStyle(tapTooltip.rating)}>{tapTooltip.rating}</span>
          </div>
        </Segment>
      )}
    </>
  );
};

export default RatingGraph;
